import React, { useLayoutEffect, useState } from 'react';
import { ArrowRight, Target } from 'lucide-react';
import { heroContent, siteConfig } from '../../data/mock';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [wordIndex, setWordIndex] = useState(-1);

  const words = heroContent.headline.split(' ');

  useLayoutEffect(() => {
    const timer = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(timer);
  }, []);

  // Animate words one by one with roll effect
  useLayoutEffect(() => {
    if (isVisible) {
      let index = 0;
      const interval = setInterval(() => {
        setWordIndex(index);
        index++;
        if (index >= words.length) {
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [isVisible, words.length]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#d0f4ed] via-[#f0fdf9] to-[#a8e6cf] opacity-80" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-[var(--brand-teal-300)] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--brand-teal-200)] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-[var(--brand-teal-400)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '4s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center overflow-hidden">
        {/* Tagline Badge - "Ally of the LinkedIn Hunter" with icon, no bold */}
        <div
          className={`inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-white/50 rounded-full px-5 py-2.5 mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Target className="w-4 h-4 text-[var(--brand-teal-600)]" />
          <span className="text-base sm:text-lg font-normal text-[var(--brand-teal-700)]">
            {heroContent.tagline}
          </span>
        </div>

        {/* Main Headline with word-by-word unfold animation */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
          {words.map((word, i) => (
            <span
              key={i}
              className={`inline-block mr-2 sm:mr-3 transition-all duration-700 ease-out ${
                word === 'People' 
                  ? 'text-[var(--brand-teal-600)]' 
                  : 'text-[var(--text-primary)]'
              } ${
                i <= wordIndex 
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : 'opacity-0 -translate-x-4 scale-95'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {word}
            </span>
          ))}
        </h1>

        {/* Subheadline */}
        <p 
          className={`text-lg sm:text-xl md:text-2xl text-[var(--text-secondary)] mb-8 max-w-3xl mx-auto transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {heroContent.subheadline}
        </p>

        {/* CTA Button - Mobile Optimized */}
        <div
          className={`transition-all duration-700 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <a
            href={siteConfig.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[var(--brand-teal-700)] hover:bg-[var(--brand-teal-800)] text-white font-semibold rounded-full px-8 py-4 shadow-lg shadow-[var(--brand-teal-600)]/20 transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl text-base sm:text-lg w-full sm:w-auto max-w-md mx-auto group"
          >
            <span>{heroContent.ctaText}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
